/**
 * Balão de áudio no desenho do WhatsApp.
 *
 * O `<audio controls>` do navegador não serve aqui: ele muda de aparência em
 * cada sistema, ocupa a largura toda e traz botões que ninguém usa numa
 * conversa (volume, velocidade, download). O WhatsApp mostra três coisas —
 * tocar, onda, tempo — e é isso que a pessoa procura.
 *
 * A onda é DECORATIVA e desenhada a partir do identificador do áudio, não do
 * som em si: ler a forma de onda real exigiria baixar e decodificar o arquivo
 * inteiro só para desenhar. O que é de verdade é a posição do cursor, que
 * acompanha o tempo tocado.
 */
import { useEffect, useRef, useState } from 'react';

const BARRAS = 34;

/** Alturas estáveis por áudio: o mesmo arquivo desenha sempre igual. */
function ondaDe(semente: string): number[] {
  let n = 0;
  for (const c of semente) n = (n * 31 + c.charCodeAt(0)) >>> 0;
  const alturas: number[] = [];
  for (let i = 0; i < BARRAS; i++) {
    n = (n * 1103515245 + 12345) >>> 0;
    alturas.push(0.25 + ((n >>> 16) % 100) / 133);
  }
  return alturas;
}

const VELOCIDADES = [1, 1.5, 2] as const;

export function BalaoDeAudio({
  url,
  semente,
  segundos,
  foto,
  transcricao,
}: {
  url: string;
  semente: string;
  segundos?: number | null;
  /** Foto de quem mandou, no círculo à direita — como no WhatsApp. */
  foto?: string | null;
  /** Transcrição, quando existe. Deixa ler o recado sem pôr no ouvido. */
  transcricao?: string | null;
}) {
  const [tocando, setTocando] = useState(false);
  const [posicao, setPosicao] = useState(0);
  const [duracao, setDuracao] = useState(segundos ?? 0);
  const [velocidade, setVelocidade] = useState(0);
  const audio = useRef<HTMLAudioElement>(null);
  const onda = ondaDe(semente);

  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const tempo = () => {
      setPosicao(el.currentTime);
      if (Number.isFinite(el.duration) && el.duration > 0) setDuracao(el.duration);
    };
    const acabou = () => {
      setTocando(false);
      setPosicao(0);
    };
    el.addEventListener('timeupdate', tempo);
    el.addEventListener('loadedmetadata', tempo);
    el.addEventListener('ended', acabou);
    return () => {
      el.removeEventListener('timeupdate', tempo);
      el.removeEventListener('loadedmetadata', tempo);
      el.removeEventListener('ended', acabou);
    };
  }, []);

  function alternar() {
    const el = audio.current;
    if (!el) return;
    if (el.paused) {
      void el.play();
      setTocando(true);
    } else {
      el.pause();
      setTocando(false);
    }
  }

  const progresso = duracao > 0 ? Math.min(1, posicao / duracao) : 0;
  const restante = Math.max(0, Math.round(duracao - posicao));
  const comecou = tocando || posicao > 0;

  /** Toque na onda leva o áudio para aquele ponto. */
  function irPara(e: React.MouseEvent<HTMLDivElement>) {
    const el = audio.current;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    const caixa = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - caixa.left) / caixa.width) * el.duration;
  }

  return (
    <div className="wa-audio-bloco">
    <div className="wa-audio">
      <button
        type="button"
        className="wa-audio-play"
        onClick={alternar}
        aria-label={tocando ? 'Pausar áudio' : 'Tocar áudio'}
      >
        {tocando ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <rect x="6" y="4.5" width="4" height="15" rx="1.2" />
            <rect x="14" y="4.5" width="4" height="15" rx="1.2" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M7 4.5l13 7.5-13 7.5z" />
          </svg>
        )}
      </button>

      <div className="wa-onda-area">
        <div className="wa-onda" onClick={irPara}>
          {onda.map((h, i) => (
            <i
              key={i}
              className={i / BARRAS <= progresso ? 'tocada' : ''}
              style={{ height: `${Math.round(h * 100)}%` }}
            />
          ))}
          <span className="wa-cursor" style={{ left: `${progresso * 100}%` }} aria-hidden />
        </div>
        <span className="wa-audio-tempo">{formatar(comecou ? restante : duracao)}</span>
      </div>

      {/* A velocidade só aparece depois que começa a tocar, como no WhatsApp:
          antes disso é um botão que não serve para nada. */}
      {comecou ? (
        <button
          type="button"
          className="wa-audio-velocidade"
          aria-label="Velocidade do áudio"
          onClick={() => {
            const proxima = (velocidade + 1) % VELOCIDADES.length;
            setVelocidade(proxima);
            if (audio.current) audio.current.playbackRate = VELOCIDADES[proxima]!;
          }}
        >
          {VELOCIDADES[velocidade]}×
        </button>
      ) : null}

      {foto ? (
        <span className="wa-audio-foto">
          <img src={foto} alt="" />
          <i aria-hidden>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0h-2a5 5 0 0 1-10 0H5zm6 8v3h2v-3h-2z" />
            </svg>
          </i>
        </span>
      ) : null}

      <audio ref={audio} src={url} preload="metadata" />
    </div>

    {transcricao ? <p className="wa-transcricao">“{transcricao}”</p> : null}
    </div>
  );
}

function formatar(s: number): string {
  if (!Number.isFinite(s) || s <= 0) return '0:00';
  return `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
}
